import { FC, useCallback, useMemo, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { TPgService } from 'services/api/paymentGateway/types';
import { EasTable } from 'ui/EasTable';
import { ETableStructureAlign, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { EIconButtons } from 'ui/EasTable/EasTableRow/EasTableValue/constants';
import { PgPage } from '../common/PgPage';
import { PgPanel } from '../common/PgPanel';
import { usePgLegalEntities, usePgServices } from '../common/hooks';
import { PG_SETTINGS_GROUP, PG_SERVICE_KIND_LABELS } from '../constants';
import { ServiceDialog } from './ServiceDialog';
import { ServiceTypeMappings } from './ServiceTypeMappings';

const EDIT_ACTION = 'Edit';

/** Settings → Services: gateway services and the EAS `service_type` mapping (03 §4.1). */
export const Services: FC = () => {
	const { services, loading } = usePgServices();
	const { legalEntities } = usePgLegalEntities();

	const [dialogOpen, setDialogOpen] = useState(false);
	const [serviceToEdit, setServiceToEdit] = useState<TPgService | null>(null);

	const tableStructure = useMemo<TTableStructure<keyof TPgService, TPgService>[]>(() => {
		const legalEntityName = (id: number) =>
			legalEntities?.find((entity) => entity.id === id)?.name ?? `#${id}`;
		return [
			{
				name: 'Sort',
				selector: 'sortOrder',
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.STRING,
				align: ETableStructureAlign.LEFT,
				value: (item) => String(item.sortOrder),
			},
			{
				name: 'Code',
				selector: 'code',
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.STRING,
				align: ETableStructureAlign.LEFT,
			},
			{
				name: 'Name',
				selector: 'name',
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.STRING,
				align: ETableStructureAlign.LEFT,
				tooltip: (item) => item.description ?? '',
			},
			{
				name: 'Kind',
				selector: 'kind',
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.STRING,
				align: ETableStructureAlign.LEFT,
				value: (item) => PG_SERVICE_KIND_LABELS[item.kind] ?? item.kind,
			},
			{
				name: 'Legal entity',
				selector: 'legalEntityId',
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.STRING,
				align: ETableStructureAlign.LEFT,
				value: (item) => legalEntityName(item.legalEntityId),
			},
			{
				name: 'EAS service types',
				selector: 'mappedServiceTypes',
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.STRING,
				align: ETableStructureAlign.LEFT,
				value: (item) =>
					item.mappedServiceTypes.length ? item.mappedServiceTypes.join(', ') : '—',
			},
			{
				name: 'Active',
				selector: 'isActive',
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.BOOLEAN,
				align: ETableStructureAlign.CENTER,
			},
			{
				name: 'Actions',
				selector: 'actions',
				noName: true,
				show: true,
				sortable: false,
				filterable: false,
				type: ETableStructureType.BUTTONS,
				align: ETableStructureAlign.RIGHT,
				buttons: [{ name: EDIT_ACTION, icon: EIconButtons.EDIT }],
			},
		];
	}, [legalEntities]);

	const handleAddNew = useCallback(() => {
		setServiceToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action !== EDIT_ACTION) return;
			setServiceToEdit(services?.find((service) => service.id === id) ?? null);
			setDialogOpen(true);
		},
		[services],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setServiceToEdit(null);
	}, []);

	return (
		<PgPage title="Services" group={PG_SETTINGS_GROUP}>
			<PgPanel
				title="Services"
				bodyPadding={0}
				actions={
					<>
						{loading && <CircularProgress size={18} />}
						<Button
							variant="contained"
							size="small"
							onClick={handleAddNew}
							disabled={!legalEntities?.length}
						>
							Add service
						</Button>
					</>
				}
			>
				<EasTable<TPgService>
					tableStructure={tableStructure}
					items={services ?? []}
					itemsIsLoading={loading && !services}
					handleAction={handleAction}
					skeletonRows={Array.from(new Array(5))}
				/>
			</PgPanel>
			<ServiceTypeMappings services={services} />
			<ServiceDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				serviceToEdit={serviceToEdit}
				legalEntities={legalEntities ?? []}
			/>
		</PgPage>
	);
};
